import { Kind, GeocoderLocationType, YesNoUnknown } from '@policyfly/protobuf'
import {
  dateToString,
  timestampToMilliseconds,
  timestampToString,
} from '@policyfly/utils/protobuf'

import { policyGrpcToJson } from './policy'
import { genericStatusToApplicationStatus } from './status'
import { coreUserToUserJson } from './user'

import type { ReadonlyPolicyState } from '@/stores/protobuf'
import type { Policy as PolicyGrpc, PolicyState } from '@policyfly/protobuf'
import type { APIApplication, ApplicationKind } from 'types/application'

/**
 * @todo Wire up remaining properties
 *
 * Converts a PolicyState to an equivalent Django Application structure.
 * Used to preserve backwards compatibility for older programs.
 */
export function policyStateToApplication (
  policyState: PolicyState | ReadonlyPolicyState,
): APIApplication {
  const policyModified = timestampToMilliseconds(policyState.modifiedAt)

  const modified = policyModified
    ? new Date(policyModified).toISOString()
    : ''

  const kind = kindToApplicationKind(policyState.kind)
  const category = (() => {
    switch (policyState.kind) {
      case Kind.KIND_CANCELLATION: return 'CAN'
      case Kind.KIND_ENDORSEMENT: return 'END'
      case Kind.KIND_EXTENSION: return 'NPB'
      case Kind.KIND_REINSTATEMENT: return 'REI'
      case Kind.KIND_RENEWAL: return 'REN'
      case Kind.KIND_EXPIRATION: return 'EXP'
      default: return 'NEW'
    }
  })()

  return {
    id: policyState.id,
    agency: policyState.agency as unknown as APIApplication['agency'],
    archived: false, // TODO: wire up
    assignee: coreUserToUserJson(policyState.assignee),
    attachments: [], // handled by attachment store
    // @ts-expect-error(external): Swagger category doesn't include 'CAN'
    category,
    childID: null, // TODO: wire up
    computedData: {}, // not relevant
    created: timestampToString(policyState.createdAt)!,
    data: {}, // not relevant
    date_entered_reviewing: null, // TODO: wire up
    derivedData: null, // TODO: wire up
    diffData: {}, // not relevant
    displayEndorsementEffectiveDate: dateToString(policyState.displayEndorsementEffectiveDate),
    effective: dateToString(policyState.effective),
    endorsement_iterator: Number(policyState.endorsementIterator || 0),
    events: [], // the feed loads its own events, see the feed store
    expiration: dateToString(policyState.expiration),
    is_cancellation: policyState.kind === Kind.KIND_CANCELLATION,
    kind,
    last_modified_by: null, // TODO: wire up
    last_rating: null, // not relevant
    last_rating_error: '', // not relevant
    license_data: null, // TODO: wire up
    lock_code: null, // TODO: wire up
    lock_description: '', // TODO: wire up
    modified,
    owned_by: coreUserToUserJson(policyState.owner),
    owner_role_in_program: '', // not relevant
    lastDescendantID: null, // TODO: wire up
    parentID: null, // TODO: wire up
    policy: policyGrpcToJson(policyState.policy ?? {} as PolicyGrpc, true) as unknown as APIApplication['policy'],
    program: policyState.program!, // TODO: wire up
    quote_set: policyState.selectedQuote ? { selected: policyState.selectedQuote.uuid4 } : {}, // TODO: wire up
    // @ts-expect-error(external): API type is wrong, it can be null
    selectedQuote: null, // TODO: wire up
    status: genericStatusToApplicationStatus(policyState.status),
    substatus: null, // TODO: wire up
    unread_comment_count: 0, // TODO: wire up
  }
}

/**
 * Converts Protobuf Kind {@link Kind}
 * to equivalent Django {@link ApplicationKind}
 */
export function kindToApplicationKind (kind: Kind): ApplicationKind {
  return kindToApplicationKindMap.get(kind) ?? 'APPLICATION'
}

const kindToApplicationKindMap = new Map<Kind, ApplicationKind>([
  [Kind.KIND_APPLICATION, 'APPLICATION'],
  [Kind.KIND_RENEWAL, 'RENEWAL'],
  [Kind.KIND_ENDORSEMENT, 'ENDORSEMENT'],
  [Kind.KIND_CANCELLATION, 'ENDORSEMENT'],
  [Kind.KIND_REINSTATEMENT, 'ENDORSEMENT'],
  [Kind.KIND_EXTENSION, 'ENDORSEMENT'],
  [Kind.KIND_EXPIRATION, 'ENDORSEMENT'],
])

/**
 * Converts Django {@link ApplicationKind}
 * to equivalent Protobuf Kind {@link Kind}
 */
export function applicationKindToKind (kind: ApplicationKind): Kind {
  return applicationKindToKindMap.get(kind) ?? Kind.KIND_APPLICATION
}

const applicationKindToKindMap = new Map<ApplicationKind, Kind>([
  ['APPLICATION', Kind.KIND_APPLICATION],
  ['RENEWAL', Kind.KIND_RENEWAL],
  ['ENDORSEMENT', Kind.KIND_ENDORSEMENT],
])

const stringToGeocoderLocationTypeMap = new Map<string, GeocoderLocationType>([
  ['ROOFTOP', GeocoderLocationType.GEOCODER_LOCATION_TYPE_ROOFTOP],
  ['RANGE_INTERPOLATED', GeocoderLocationType.GEOCODER_LOCATION_TYPE_RANGE_INTERPOLATED],
  ['GEOMETRIC_CENTER', GeocoderLocationType.GEOCODER_LOCATION_TYPE_GEOMETRIC_CENTER],
  ['APPROXIMATE', GeocoderLocationType.GEOCODER_LOCATION_TYPE_APPROXIMATE],
])

/**
 * Converts Google Geocode API location type to Protobuf {@link GeocoderLocationType}
 */
export function stringToGeocoderLocationType (locationType: string): GeocoderLocationType {
  return stringToGeocoderLocationTypeMap.get(locationType) ?? GeocoderLocationType.GEOCODER_LOCATION_TYPE_UNKNOWN
}

const yesNoUnknownToBooleanMap = new Map<YesNoUnknown, boolean>([
  [YesNoUnknown.ANSWER_YES, true],
  [YesNoUnknown.ANSWER_NO, false],
])

const booleanToYesNoUnknownMap = new Map<boolean, YesNoUnknown>([
  [true, YesNoUnknown.ANSWER_YES],
  [false, YesNoUnknown.ANSWER_NO],
])

export function yesNoUnknownToBoolean (value: YesNoUnknown): boolean | null {
  return yesNoUnknownToBooleanMap.get(value) ?? null
}

export function booleanToYesNoUnknown (value: boolean | null): YesNoUnknown {
  return value === null
    ? YesNoUnknown.ANSWER_UNSPECIFIED
    : booleanToYesNoUnknownMap.get(value) ?? YesNoUnknown.ANSWER_UNKNOWN
}
